import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import InputSettingsField from './InputSettingsField';
import FieldStyleSettings from './FieldStyleSettings';
import MetadataSettings from './MetadataSettings';
import Dropzone from '../../misc/Dropzone';
import AccordionSettings from './AccordionSettings';
import CheckboxSettingsField from './CheckboxSettingsField';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import { StyledFieldWrapper, StyledAccordionFieldWrapper, StyledAddonAfter } from './SettingsCommonElements';
import Icon from '../../misc/Icon';
import ThemeIcons from '../../../theme/Icons';
import FileViews from '../../../constants/FileViews';
import FormStylesOptions from '../../../constants/FormStylesOptions';
import FormulaTestsModal from '../formulas/FormulaTestsModal';
import FormulaTestsSelector from '../formulas/FormulaTestsSelector';
import { formulas } from '../formulas';

import FieldModes from '../../../constants/FieldModes';
import FieldPresentationModes from '../../../constants/FieldPresentationModes';
import PageLayoutModes from '../../../constants/PageLayoutModes';

import { getFieldThemeObject } from '../../../theme/getThemeObject';

import ThemeContext from '../../../theme/ThemeContext';
import { isSettingHidden, isStyleSettingHidden, getMetadataOptions } from '../../../lib/settingsHelper';

import Popconfirm from '../../misc/Popconfirm';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledMirrorSelector = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledMirrorTrigger = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  gap: 8px;
  width: 100%;
  justify-content: space-between;
  
  &:hover {
    border-color: ${(props) => props.theme.primaryColor};
  }
`;

const StyledMirrorContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledLabel = styled.div`
  display: flex;
  flex: 1;
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme.fontColorPrimary};
  margin-bottom: 6px;
`;

const StyledPopoverMenu = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  margin-bottom: 8px;
  background: ${(props) => props.theme.backgroundColor};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  z-index: 1000;
`;

const StyledMenuItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${(props) => props.theme.fontColorPrimary};
  gap: 8px;
  
  &:hover {
    background-color: ${(props) => props.theme.secondaryColor};
  }

  ${(props) => props.selected && `
    color: ${props.theme.primaryColor};
    background-color: ${props.theme.secondaryColor};
  `}
`;

const StyledMenuItemLabel = styled.span`
  font-size: 14px;
`;

const StyledFormulaButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 4px;
  background-color: ${(props) => props.theme.backgroundColor};
  color: ${(props) => props.theme.fontColorPrimary};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  width: 100%;
  
  &:hover {
    border-color: ${(props) => props.theme.primaryColor};
    background-color: ${(props) => props.theme.secondaryColor};
  }
`;

const StyledFormulaSection = styled.div`
  margin-bottom: 16px;
  padding: 12px 0;
  border-top: 1px solid ${(props) => props.theme.borderColor};
`;

const FileSettings = ({

  id,
  name,
  styles,
  header,
  footer,
  metadata,
  features,
  config = {},

  //Action handlers
  onChange,
  onUpload,

  //View helpers
  view,
  hasMobileView,
  onToggleViewSync,

  doc,
  onDocumentChange,
  
}) => {

  const theme = useContext(ThemeContext);

  const {
    //Title styles
    titleFontSize,
    titleFontColor,
    titleTextAlign,
    titleFontStyle,
    titleFontWeight,
    titleTextTransform,
    titleTextDecoration,

    //Field style
    fontSize,
    fontColor,
    fontStyle,
    fontWeight,
    textAlign,
    textTransform,
    textDecoration,
    textOverflow,
    borderColor,
    borderRadius,
    borderWidth,
    backgroundColor,
    padding,
    margin
  } = getFieldThemeObject({
    mode: FieldModes.fill,
    fieldPresentationMode: FieldPresentationModes.normal,
    fileStyles: styles,
    theme: theme?.field,
    sdkTheme: theme
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedMirrorView, setSelectedMirrorView] = useState(!hasMobileView ? FileViews.desktop : 'none');
  const [displayConfirm, setDisplayConfirm] = useState(false);

  // Formula testing state
  const [selectedExample, setSelectedExample] = useState('default');
  const [selectedExampleData, setSelectedExampleData] = useState(null);
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [formulaDebugData, setFormulaDebugData] = useState(null);

  const handleHeaderFooterChange = (key, changes) => {
    if (onChange) {
      const currentHeaderFooter = key === 'header' ? header : footer;
      onChange(id, { [key]: { ...currentHeaderFooter, ...changes } });
    }
  }

  const handleToggleHeaderFooter = (key) => {

    const currentHeaderFooter = key === 'header' ? header : footer;

    let changes = {}

    if (currentHeaderFooter) {

      // If it exists, we need to remove it
      changes = { [key]: null };

    } else {

      // If it doesn't exist, we need to create it with default values
      changes = {
        [key]: {
          height: 100,
          padding: 0,
          cols: 24,
          rowHeight: 1,
          layout: PageLayoutModes.grid,
          fieldPositions: []
        }
      };
    }

    if (onChange) {
      onChange(id, changes);
    }

  }

  // Formula testing handlers
  const handleFormulaResolve = () => {
    if (!doc) return;
    
    // Use the currently selected example data or the actual doc
    const docToUse = selectedExampleData || doc;
    
    // Show the modal in preview mode (no resolution yet)
    const debugData = {
      beforeDoc: docToUse,
      afterResult: null, // No result yet - modal will handle resolution
      executionTime: null
    };
    
    setFormulaDebugData(debugData);
    setDebugModalOpen(true);
  };

  const handleFormulaChange = (formulaChanges) => {
    if (!onDocumentChange || !doc) return;
    
    // Update the formulas in the document
    const updatedDoc = JSON.parse(JSON.stringify(doc));
    
    formulaChanges.forEach(({ formulaId, expression }) => {
      if (updatedDoc.formulas) {
        const formulaIndex = updatedDoc.formulas.findIndex(f => f._id === formulaId);
        if (formulaIndex !== -1) {
          updatedDoc.formulas[formulaIndex].expression = expression;
        }
      }
    });
    
    onDocumentChange(updatedDoc);
    
    // Update the modal's beforeDoc with the new document
    setFormulaDebugData(prevData => ({
      ...prevData,
      beforeDoc: updatedDoc
    }));
  };

  const handleApplyFormulaChanges = (resolvedDoc) => {
    if (onDocumentChange) {
      onDocumentChange(resolvedDoc);
    }
  };

  const handleJoydocChange = (updatedDoc) => {
    if (onDocumentChange) {
      onDocumentChange(updatedDoc);
      
      // Update the modal's beforeDoc with the new document
      setFormulaDebugData(prevData => ({
        ...prevData,
        beforeDoc: updatedDoc
      }));
    }
  };

  const handleExampleChange = (key, data) => {

    setSelectedExample(key);
    
    setSelectedExampleData(data);
    
    if (data && onDocumentChange) {
      // If an example is selected, update the document to use the example data
      onDocumentChange(data);
    }
  };

  //Settings
  const isTitleHidden = isSettingHidden(config, 'title');
  const isUploadHidden = isSettingHidden(config, 'upload');

  //Header/Footer Settings
  const isHeaderHidden = features?.repeatingHeaderAndFooter ? isSettingHidden(config, 'header') : true;
  const isHeaderEnabled = !!header;
  const isFooterHidden = features?.repeatingHeaderAndFooter ? isSettingHidden(config, 'footer') : true;
  const isFooterEnabled = !!footer;

  //Title Styles
  const isTitleFontSizeHidden = isStyleSettingHidden(config, 'titleFontSize');
  const isTitleFontColorHidden = isStyleSettingHidden(config, 'titleFontColor');
  const isTitleTextAlignHidden = isStyleSettingHidden(config, 'titleTextAlign');
  const isTitleFontStyleHidden = isStyleSettingHidden(config, 'titleFontStyle');
  const isTitleFontWeightHidden = isStyleSettingHidden(config, 'titleFontWeight');
  const isTitleTextTransformHidden = isStyleSettingHidden(config, 'titleTextTransform');
  const isTitleTextDecorationHidden = isStyleSettingHidden(config, 'titleTextDecoration');

  //Text Styles
  const isFontSizeHidden = isStyleSettingHidden(config, 'fontSize');
  const isFontColorHidden = isStyleSettingHidden(config, 'fontColor');
  const isTextAlignHidden = isStyleSettingHidden(config, 'textAlign');
  const isFontStyleHidden = isStyleSettingHidden(config, 'fontStyle');
  const isFontWeightHidden = isStyleSettingHidden(config, 'fontWeight');
  const isTextTransformHidden = isStyleSettingHidden(config, 'textTransform');
  const isTextDecorationHidden = isStyleSettingHidden(config, 'textDecoration');
  const isTextOverflowHidden = isStyleSettingHidden(config, 'textOverflow');

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(config, 'backgroundColor');
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');

  const metadataOptions = getMetadataOptions(config);
  const popoverTitle = !hasMobileView ? 'Disable Mirroring Desktop' : 'Mirror Desktop';
  const popoverDescription = !hasMobileView ? 'You will no longer receive changes from the desktop view. Mobile view will be independent. Are you sure you want to continue?'
    : 'You will lose any changes made to the mobile layout view. Are you sure you want to continue?';

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle title={view === FileViews.mobile ? 'Mobile Settings' : 'Settings'} />
      {isTitleHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId="name"
            value={name}
            type="text"
            onChange={(fieldId, value) => onChange(id, { name: value })}
          />
        </StyledFieldWrapper>
      )}
      {view !== FileViews.mobile ? null : (
        <StyledFieldWrapper>
          <StyledLabel>Mirror view</StyledLabel>
          <StyledMirrorSelector>
            <StyledMirrorTrigger
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              theme={theme}
            >
              <StyledMirrorContent>
                <Icon
                  icon={ThemeIcons.misc.refresh}
                  color={theme.fontColorPrimary}
                  size={14}
                />
                <span>{!hasMobileView ? 'Desktop' : 'None'}</span>
              </StyledMirrorContent>
              <Icon
                icon={ThemeIcons.misc.chevronDown}
                size={14}
                color={theme.fontColorSecondary}
              />
            </StyledMirrorTrigger>

            {isMenuOpen && (
              <StyledPopoverMenu theme={theme}>
                <StyledMenuItem
                  theme={theme}
                  selected={!hasMobileView}
                  onClick={() => {
                    setSelectedMirrorView(FileViews.desktop);
                    setIsMenuOpen(false);
                    setDisplayConfirm(true);
                  }}
                >
                  <Icon
                    icon={ThemeIcons.settings.webScreen}
                    color={theme.fontColorPrimary}
                  />
                  <StyledMenuItemLabel>Desktop</StyledMenuItemLabel>
                </StyledMenuItem>
                <StyledMenuItem
                  theme={theme}
                  selected={hasMobileView}
                  onClick={() => {
                    setSelectedMirrorView('none');
                    setIsMenuOpen(false);
                    setDisplayConfirm(true);
                  }}
                >
                  <Icon
                    icon={ThemeIcons.misc.close}
                    color={theme.fontColorPrimary}
                    size="lg"
                  />
                  <StyledMenuItemLabel style={{ marginLeft: 5 }}>None</StyledMenuItemLabel>
                </StyledMenuItem>
              </StyledPopoverMenu>
            )}
          </StyledMirrorSelector>
          <Popconfirm
            title={popoverTitle}
            description={popoverDescription}
            isOpen={displayConfirm}
            positions={['left']}
            padding={18}
            cancelText="Cancel"
            onCancel={() => {
              setSelectedMirrorView(!hasMobileView ? 'desktop' : 'none');
              setDisplayConfirm(false);
            }}
            confirmText="Confirm"
            containerStyles={{ lineHeight: '16px' }}
            onConfirm={() => {
              onToggleViewSync(FileViews.mobile);
              setDisplayConfirm(false);
            }}
          >
            <div />
          </Popconfirm>
        </StyledFieldWrapper>
      )}

      {isUploadHidden || !onUpload || view === FileViews.mobile ? null : (
        <StyledFieldWrapper>
          <Dropzone
            dragMessage="pdf, png, jpg"
            onDrop={(files) => onUpload(id, files[0])}
            accept={{
              'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
              'image/png': ['.png', '.PNG'],
              'application/pdf': ['.pdf', '.PDF']
            }}
          />
        </StyledFieldWrapper>
      )}
      {!metadataOptions ? null : (
        <>
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(id, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      
      {(isTitleFontSizeHidden
        && isTitleFontColorHidden
        && isTitleTextAlignHidden
        && isTitleFontStyleHidden
        && isTitleFontWeightHidden
        && isTitleTextTransformHidden
        && isTitleTextDecorationHidden) ? null : (
          <AccordionSettings
            isOpen
            title="Title"
            height={156}
          >
            <FieldStyleSettings
              id={`${id}-title`}
              {...{
                fontSize: titleFontSize,
                onFontSizeChange: isTitleFontSizeHidden ? null : (value) => onChange(id, { styles: { ...styles, titleFontSize: value } }),

                fontColor: titleFontColor,
                onFontColorChange: isTitleFontColorHidden ? null : (value) => onChange(id, { styles: { ...styles, titleFontColor: value } }),

                textAlign: titleTextAlign,
                onTextAlignChange: isTitleTextAlignHidden ? null : (value) => onChange(id, { styles: { ...styles, titleTextAlign: value } }),

                fontStyle: titleFontStyle,
                onFontStyleChange: isTitleFontStyleHidden ? null : (value) => onChange(id, { styles: { ...styles, titleFontStyle: value } }),

                fontWeight: titleFontWeight || FormStylesOptions.fontWeightBold,
                onFontWeightChange: isTitleFontWeightHidden ? null : (value) => onChange(id, { styles: { ...styles, titleFontWeight: value } }),

                textTransform: titleTextTransform,
                onTextTransformChange: isTitleTextTransformHidden ? null : (value) => onChange(id, { styles: { ...styles, titleTextTransform: value } }),

                textDecoration: titleTextDecoration,
                onTextDecorationChange: isTitleTextDecorationHidden ? null : (value) => onChange(id, { styles: { ...styles, titleTextDecoration: value } }),
              }}
            />
          </AccordionSettings>
        )}
      {(
        isFontSizeHidden
        && isFontColorHidden
        && isTextAlignHidden
        && isFontStyleHidden
        && isFontWeightHidden
        && isTextTransformHidden
        && isTextDecorationHidden
        && isTextOverflowHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Text"
          height={156}
        >
          <FieldStyleSettings
            id={`${id}-field`}
            {...{

              fontSize,
              onFontSizeChange: isFontSizeHidden ? null : (value) => onChange(id, { styles: { ...styles, fontSize: value } }),

              fontColor,
              onFontColorChange: isFontColorHidden ? null : (value) => onChange(id, { styles: { ...styles, fontColor: value } }),

              textAlign,
              onTextAlignChange: isTextAlignHidden ? null : (value) => onChange(id, { styles: { ...styles, textAlign: value } }),

              fontStyle,
              onFontStyleChange: isFontStyleHidden ? null : (value) => onChange(id, { styles: { ...styles, fontStyle: value } }),

              fontWeight,
              onFontWeightChange: isFontWeightHidden ? null : (value) => onChange(id, { styles: { ...styles, fontWeight: value } }),

              textTransform,
              onTextTransformChange: isTextTransformHidden ? null : (value) => onChange(id, { styles: { ...styles, textTransform: value } }),

              textDecoration,
              onTextDecorationChange: isTextDecorationHidden ? null : (value) => onChange(id, { styles: { ...styles, textDecoration: value } }),

              textOverflow,
              onTextOverflowChange: isTextOverflowHidden ? null : (value) => onChange(id, { styles: { ...styles, textOverflow: value } }),

            }}
          />
        </AccordionSettings>
        )}
      {(
        isBorderColorHidden
        && isBorderRadiusHidden
        && isBorderWidthHidden
        && isBackgroundColorHidden
        && isMarginHidden
        && isPaddingHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Field"
          height={200}
        >
          <FieldStyleSettings
            id={`${id}-field`}
            {...{
              borderColor,
              onBorderColorChange: isBorderColorHidden ? null : (value) => onChange(id, { styles: { ...styles, borderColor: value } }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden ? null : (value) => onChange(id, { styles: { ...styles, borderRadius: value } }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden ? null : (value) => onChange(id, { styles: { ...styles, borderWidth: value } }),

              backgroundColor,
              onBackgroundColorChange: isBackgroundColorHidden ? null : (value) => onChange(id, { styles: { ...styles, backgroundColor: value } }),

              margin,
              onMarginChange: isMarginHidden ? null : (value) => onChange(id, { styles: { ...styles, margin: value } }),

              padding,
              onPaddingChange: isPaddingHidden ? null : (value) => onChange(id, { styles: { ...styles, padding: value } }),

            }}
          />
        </AccordionSettings>
        )}
        {isHeaderHidden ? null : (
          <AccordionSettings
            isOpen={false}
            title="Repeating Header"
            height={256}
          >
            <StyledAccordionFieldWrapper>
              <CheckboxSettingsField
                id="enableHeader"
                label="Enable Header"
                description="Only supported in desktop view"
                selected={!!header}
                onChange={() => handleToggleHeaderFooter('header')}
              />
            </StyledAccordionFieldWrapper>
            {!isHeaderEnabled ? null : (
              <>
                <StyledAccordionFieldWrapper>
                  <InputSettingsField
                    value={header?.height}
                    addonAfter={<StyledAddonAfter>Height</StyledAddonAfter>}
                    type="number"
                    onChange={(fieldId, value) => handleHeaderFooterChange('header', { height: parseInt(value, 10) })}
                  />
                </StyledAccordionFieldWrapper>
                <FieldStyleSettings
                  id="page-field"
                  {...{
                    padding: header.padding,
                    onPaddingChange: (value) => handleHeaderFooterChange('header', { padding: parseInt(value, 10) }),
                  }}
                />
              </>
            )}
          </AccordionSettings>
        )}
        {isFooterHidden ? null : (
          <AccordionSettings
            isOpen={false}
            title="Repeating Footer"
            height={256}
          >
            <StyledAccordionFieldWrapper>
              <CheckboxSettingsField
                id="enableFooter"
                label="Enable Footer"
                description="Only supported in desktop view"
                selected={!!footer}
                onChange={() => handleToggleHeaderFooter('footer')}
              />
            </StyledAccordionFieldWrapper>
            {!isFooterEnabled ? null : (
              <>
                <StyledAccordionFieldWrapper>
                  <InputSettingsField
                    value={footer?.height}
                    addonAfter={<StyledAddonAfter>Height</StyledAddonAfter>}
                    type="number"
                    onChange={(fieldId, value) => handleHeaderFooterChange('footer', { height: parseInt(value, 10) })}
                  />
                </StyledAccordionFieldWrapper>
                <FieldStyleSettings
                  id="page-field"
                  {...{
                    padding: footer.padding,
                    onPaddingChange: (value) => handleHeaderFooterChange('footer', { padding: parseInt(value, 10) }),
                  }}
                />
              </>
            )}
          </AccordionSettings>
        )}
      {/* Formula Testing Section - only show when sandbox is enabled */}
      {features?.formulas ? (
        <StyledFormulaSection theme={theme}>
          <StyledFieldWrapper>
            <StyledLabel>Formulas</StyledLabel>
            <StyledFormulaButton
              onClick={handleFormulaResolve}
              theme={theme}
              >
              Modify Formulas
            </StyledFormulaButton>
          </StyledFieldWrapper>
          {features?.formulasSandbox ? (
            <StyledFieldWrapper>
              <FormulaTestsSelector
                value={selectedExample}
                onChange={handleExampleChange}
              />
            </StyledFieldWrapper>
          ) : null}
        </StyledFormulaSection>
        ) : null}
        <FormulaTestsModal
          isOpen={debugModalOpen}
          onClose={() => setDebugModalOpen(false)}
          beforeDoc={formulaDebugData?.beforeDoc}
          afterResult={formulaDebugData?.afterResult}
          executionTime={formulaDebugData?.executionTime}
          onApplyChanges={handleApplyFormulaChanges}
          onFormulaChange={handleFormulaChange}
          onJoydocChange={handleJoydocChange}
          useReadableIds={features?.readableIds}
        />
      
    </StyledWrapper>
  );

};

export default FileSettings;